/*
 * Copyright (c) 2019 Pegasystems Inc.
 * All rights reserved.
 */
import { ofType } from 'redux-observable';
import { catchError, mergeMap, takeUntil, filter } from 'rxjs/operators';
import { of, Observable, empty } from 'rxjs';
import isEqual from 'lodash.isequal';
import type { OperatorFunction, ObservedValueOf } from 'rxjs';

import * as actions from '../store/state/actions/action-creators';
import type { ActionError } from '../store/state/actions/action-creators';

/**
 * this function is a wrapper around catchError
 * --> helps to log the error and returns error action object
 *
 * @param msg message for the error.
 * @param context context for the error
 * @returns the error action object.
 */
export const logAndCatchError = (
  msg: string,
  context?: string,
  actionMgrID?: string
): OperatorFunction<any, any | ObservedValueOf<Observable<ActionError>>> =>
  catchError((error: Observable<ActionError>) => {
    if ((error as { code?: string })?.code !== 'ERR_CANCELED') {
      console.error(msg, error);
    }
    /* if (context && context.actionMgrID) {
    ActionManagerInstance.rejectActionByID(context.actionMgrID)
  } */
    return of(actions.error(msg, error, context, null, actionMgrID));
  });

interface RequestPayload {
  signal: { cancel: (reason: string) => void };
  [key: string]: unknown;
}

type ComparatorFn = (a: unknown, b: unknown) => boolean;

/**
 * this function is a wrapper around takeUntil
 * --> helps to cancel the original request if CANCEL_ACTION was triggered
 *
 * @param action$ observable stream
 * @param reqPayload request Payload
 * @param comparator function to compare properties between cancel request and normal request payload
 * @returns outer observable
 */
export const continueRequestUntil = (
  action$: Observable<{ type: string; id: string }>,
  reqPayload: RequestPayload,
  comparator: ComparatorFn
) =>
  takeUntil(
    action$.pipe(
      ofType('CANCEL_ACTION'),
      filter((cancelRequest) => {
        let cancel = false;
        if (comparator && comparator(cancelRequest, reqPayload)) {
          reqPayload.signal.cancel('cancel');
          cancel = true;
        }
        return cancel;
      })
    )
  );

/**
 * --> helps to throttle based on time and distinct payload
 *
 * @param delay number of milliseconds for debounce
 * @param comparator function to compare properties between prev request and next request payload
 * @returns 1) empty observable within delay time. OR
 *          2) actual value if delay time crosses.
 */
export const throttleUntilChanged = <T>(
  delay: number,
  comparator?: ComparatorFn
): ((source: Observable<T>) => Observable<T>) => {
  return (source: Observable<T>) => {
    return new Observable((observer) => {
      let lastSeen: T | Record<string, never> = {};
      let lastSeenTime = 0;

      return source
        .pipe(
          mergeMap((value: T) => {
            const now = Date.now();
            let currentComparator;
            if (comparator) {
              currentComparator = comparator;
            } else {
              currentComparator = isEqual;
            }

            if (currentComparator && currentComparator(value, lastSeen) && now - lastSeenTime < delay) {
              return empty();
            }
            lastSeen = value;
            lastSeenTime = now;
            return of(value);
          })
        )
        .subscribe(observer);
    });
  };
};
